import { useEffect, useMemo, useRef, useState } from 'react';
import type { PatientItem } from '../../components/psychologist/patients/types';
import apiCoreAdapter from '../apiCoreAdapter';

export interface PatientDetailData extends PatientItem {
	isActive: boolean;
	automaticDebit?: boolean;
	paymentType?: 'pack' | 'unico';
	profile?: {
		birthDate?: string;
		country?: string;
		timezone?: string;
		state?: string;
	};
	totalSessions?: number;
	isUrgent?: boolean;
	urgentMarkedAt?: string | null;
	phone?: string | null;
}

export function usePatientDetail(processId?: string, patientId?: string) {
	const [data, setData] = useState<PatientDetailData | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingSessions, setIsLoadingSessions] = useState<boolean>(false);
	const [isError, setIsError] = useState<boolean>(false);
	const sessionsLoadedForRef = useRef<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		async function loadInfo() {
			if (!processId) { setData(null); return; }
			setIsLoading(true);
			setIsError(false);
			try {
				let mapped: PatientDetailData | null = null;
				// Intentar por processId
				try {
					const resp = await apiCoreAdapter.psychologistPatientByProcess(processId);
					if (!cancelled) {
						const r = resp as unknown as {
							process_id?: string;
							full_name?: string;
							email?: string;
							therapy_type?: string;
							is_active?: boolean;
							automatic_debit?: boolean;
							payment_type?: string;
							birthdate?: string;
							nationality?: string;
							timezone?: string;
							address?: { country?: string; state?: string; city?: string; formatted_address?: string };
							total_sessions?: number;
							reason?: string;
							is_urgent?: boolean;
							urgent_marked_at?: string | null;
							phone?: string | null;
						} | null;
						if (r) {
							const fullname = r.full_name || '';
							const [name, ...rest] = fullname.split(' ');
							const surname = rest.join(' ').trim();
							mapped = {
								id: r.process_id || processId,
								process: r.process_id || processId,
								name: name || null,
								surname: surname || null,
								email: r.email || '',
								fullname,
								therapyType: r.therapy_type || undefined,
								lastSessionDate: undefined,
								nextSessionDate: undefined,
								status: r.is_active ? 'active' : 'inactive',
								isActive: !!r.is_active,
								automaticDebit: !!r.automatic_debit,
								paymentType: r.payment_type === 'pack' ? 'pack' : (r.payment_type === 'unico' ? 'unico' : undefined),
								profile: {
									birthDate: r.birthdate,
									country: r.address?.country || undefined,
									timezone: r.timezone || undefined,
									state: r.address?.state || undefined,
								},
								totalSessions: r.total_sessions,
								reason: r.reason,
								isUrgent: r.is_urgent ?? false,
								urgentMarkedAt: r.urgent_marked_at ?? null,
								phone: r.phone ?? null,
							};
						}
					}
				} catch {
					// continuar a fallback
				}
				// Fallback: intentar por patient id
				if (!mapped) {
					const resp2 = await apiCoreAdapter.patientDetail(patientId || processId);
					if (!cancelled && resp2) {
						const p = resp2 as unknown as {
							id?: string;
							name?: string;
							surname?: string;
							email?: string;
							timezone?: string;
						};
						const fullname = [p.name, p.surname].filter(Boolean).join(' ').trim();
						mapped = {
							id: p.id || processId,
							process: processId,
							name: p.name || null,
							surname: p.surname || null,
							email: p.email || '',
							fullname,
							therapyType: undefined,
							lastSessionDate: undefined,
							nextSessionDate: undefined,
							status: 'active',
							isActive: true,
							profile: {
								timezone: p.timezone
							},
						} as PatientDetailData;
					}
				}
				if (!cancelled) {
					if (!mapped) {
						setIsError(true);
						setData(null);
					} else {
						setData(mapped);
					}
					setIsLoading(false);
				}

				// Info ya cargada, ahora cargar sesiones en background (una sola vez por processId)
				if (mapped && !cancelled && sessionsLoadedForRef.current !== processId) {
					sessionsLoadedForRef.current = processId;
					loadSessions(mapped, processId, cancelled);
				}
			} catch {
				if (!cancelled) {
					setIsError(true);
					setData(null);
					setIsLoading(false);
				}
			}
		}

		async function loadSessions(current: PatientDetailData, pid: string, alreadyCancelled: boolean) {
			if (alreadyCancelled) return;
			setIsLoadingSessions(true);
			try {
				const history = await apiCoreAdapter.psychologistPatientSessionsHistory(pid);
				if (cancelled || !Array.isArray(history)) return;

				// Notas legadas en sessions.notes anteriores a mediados de 2025 son ruido
				// histórico no confiable: ocultamos el contenido aunque la sesión exista.
				const NOTES_VISIBLE_FROM = new Date('2025-07-01T00:00:00').getTime();

				const processHistory = history.filter(h => h.process_id === pid);
				const sessionsFromHistory = processHistory.map((h, idx) => {
					const dateMs = new Date((h.date || '').replace(' ', 'T')).getTime();
					const noteVisible = !Number.isNaN(dateMs) && dateMs >= NOTES_VISIBLE_FROM;
					const rawHasNote = !!(h.notes && String(h.notes).trim().length > 0);
					return {
						id: h.sessionId || h.session_id || `${pid}-${idx}-${h.date}`,
						date: h.date,
						hasNote: noteVisible && rawHasNote,
						note: noteVisible ? (h.notes ?? undefined) : undefined,
						status: h.status || undefined,
					};
				});

				// Timezone autoritativo del paciente: tomado de las sesiones (la primera que
				// lo traiga). Si el backend no lo manda, no tocamos el que ya estaba en mapped.
				const sessionTimezone = processHistory.find(h => !!h.patient_timezone)?.patient_timezone;

				if (!cancelled) {
					setData(prev => {
						if (!prev) return prev;
						const next = { ...prev, sessions: sessionsFromHistory };
						if (sessionTimezone) {
							next.profile = { ...prev.profile, timezone: sessionTimezone };
						}
						return next;
					});
				}
			} catch {
				// ignore session load errors
			} finally {
				if (!cancelled) setIsLoadingSessions(false);
			}
		}

		loadInfo();
		return () => { cancelled = true; };
	}, [processId, patientId]);

	return useMemo(() => ({ data, isLoading, isLoadingSessions, isError }), [data, isLoading, isLoadingSessions, isError]);
}
